import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import type {
  ApiResponse,
  PaginationMeta,
} from '@core/models/api-response.model';

export interface PagedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

/**
 * Thin HttpClient wrapper. Unwraps the API envelope so callers receive the
 * `data` payload directly. Transport/HTTP errors propagate as HttpErrorResponse
 * (handled by the auth interceptor and feature components).
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  get<T>(path: string, params?: QueryParams): Observable<T> {
    return this.http
      .get<ApiResponse<T>>(this.base + path, {
        params: this.toParams(params),
      })
      .pipe(map((res) => this.unwrap(res)));
  }

  /** GET a paginated list — returns both the items and pagination meta. */
  getPage<T>(path: string, params?: QueryParams): Observable<PagedResult<T>> {
    return this.http
      .get<ApiResponse<T[]>>(this.base + path, { params: this.toParams(params) })
      .pipe(
        map((res) => {
          if (!res.success) {
            throw new Error(res.error?.message ?? 'Request failed');
          }
          return {
            items: res.data,
            pagination:
              res.pagination ??
              {
                page: 1,
                limit: res.data.length,
                total: res.data.length,
                totalPages: 1,
                hasNext: false,
                hasPrev: false,
              },
          };
        }),
      );
  }

  /**
   * GET a list response but also expose the envelope `meta` (e.g. a summary
   * block the platform schools endpoint returns alongside the array).
   */
  getListWithMeta<T, M = Record<string, unknown>>(
    path: string,
    params?: QueryParams,
  ): Observable<{ items: T[]; pagination: PaginationMeta; meta: M }> {
    return this.http
      .get<ApiResponse<T[]>>(this.base + path, { params: this.toParams(params) })
      .pipe(
        map((res) => {
          if (!res.success) {
            throw new Error(res.error?.message ?? 'Request failed');
          }
          return {
            items: res.data,
            pagination:
              res.pagination ??
              {
                page: 1,
                limit: res.data.length,
                total: res.data.length,
                totalPages: 1,
                hasNext: false,
                hasPrev: false,
              },
            meta: (res.meta ?? {}) as M,
          };
        }),
      );
  }

  post<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(this.base + path, body)
      .pipe(map((res) => this.unwrap(res)));
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(this.base + path, body)
      .pipe(map((res) => this.unwrap(res)));
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.http
      .patch<ApiResponse<T>>(this.base + path, body)
      .pipe(map((res) => this.unwrap(res)));
  }

  delete<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .delete<ApiResponse<T>>(this.base + path, body ? { body } : undefined)
      .pipe(map((res) => this.unwrap(res)));
  }

  private unwrap<T>(res: ApiResponse<T>): T {
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Request failed');
    }
    return res.data;
  }

  private toParams(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      }
    }
    return httpParams;
  }
}
